// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Rendering, geometry and export for the board. The board lives in a fixed
 * LOGICAL coordinate space (BOARD_W × BOARD_H); each client fits that space to
 * its own canvas so everyone sees the identical composition at their own size.
 *
 * The geometry helpers (fit, hit-testing) and the SVG serialiser are pure and
 * unit-tested; the canvas painters take a 2D context.
 */
import { BOARD_W, BOARD_H } from './board.ts';
import type { Shape } from './types.ts';

/** A fit transform mapping logical board coords → canvas CSS px (letterboxed). */
export interface Fit {
  scale: number;
  ox: number;
  oy: number;
}

/** Compute the fit that centres the whole board inside a cw×ch CSS-px canvas. */
export function fitBoard(cw: number, ch: number): Fit {
  const scale = Math.min(cw / BOARD_W, ch / BOARD_H) || 0;
  return { scale, ox: (cw - BOARD_W * scale) / 2, oy: (ch - BOARD_H * scale) / 2 };
}

/** Screen (CSS px, canvas-relative) → logical board coords. */
export function screenToLogical(sx: number, sy: number, fit: Fit): { x: number; y: number } {
  return { x: (sx - fit.ox) / fit.scale, y: (sy - fit.oy) / fit.scale };
}

/** Logical board coords → screen (CSS px, canvas-relative). */
export function logicalToScreen(lx: number, ly: number, fit: Fit): { x: number; y: number } {
  return { x: fit.ox + lx * fit.scale, y: fit.oy + ly * fit.scale };
}

/** Clamp a logical point into the board rectangle. */
export function clampToBoard(x: number, y: number): { x: number; y: number } {
  return { x: Math.max(0, Math.min(BOARD_W, x)), y: Math.max(0, Math.min(BOARD_H, y)) };
}

/** Distance from point (px,py) to the segment (ax,ay)-(bx,by). */
export function distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
}

/** The min distance from a point to any part of a shape's outline (logical units). */
export function distToShape(s: Shape, px: number, py: number): number {
  const p = s.points;
  if (s.kind === 'text') {
    const fs = s.fontSize ?? 32;
    const w = Math.max(fs, (s.text?.length ?? 1) * fs * 0.55);
    const x0 = p[0];
    const y0 = p[1] - fs;
    const inside = px >= x0 && px <= x0 + w && py >= y0 && py <= y0 + fs * 1.3;
    if (inside) return 0;
    const cx = Math.max(x0, Math.min(x0 + w, px));
    const cy = Math.max(y0, Math.min(y0 + fs * 1.3, py));
    return Math.hypot(px - cx, py - cy);
  }
  if (s.kind === 'rect') {
    const [x0, y0, x1, y1] = p;
    const xa = Math.min(x0, x1);
    const xb = Math.max(x0, x1);
    const ya = Math.min(y0, y1);
    const yb = Math.max(y0, y1);
    return Math.min(
      distToSegment(px, py, xa, ya, xb, ya),
      distToSegment(px, py, xb, ya, xb, yb),
      distToSegment(px, py, xb, yb, xa, yb),
      distToSegment(px, py, xa, yb, xa, ya),
    );
  }
  if (s.kind === 'ellipse') {
    const [x0, y0, x1, y1] = p;
    const cx = (x0 + x1) / 2;
    const cy = (y0 + y1) / 2;
    const rx = Math.abs(x1 - x0) / 2 || 1;
    const ry = Math.abs(y1 - y0) / 2 || 1;
    // Approximate: normalised radial distance, scaled back by the mean radius.
    const nx = (px - cx) / rx;
    const ny = (py - cy) / ry;
    const r = Math.hypot(nx, ny);
    return Math.abs(r - 1) * ((rx + ry) / 2);
  }
  // pen / line / arrow — nearest of the point-to-point segments.
  let best = Infinity;
  if (p.length < 4) {
    return p.length >= 2 ? Math.hypot(px - p[0], py - p[1]) : Infinity;
  }
  for (let i = 0; i + 3 < p.length; i += 2) {
    best = Math.min(best, distToSegment(px, py, p[i], p[i + 1], p[i + 2], p[i + 3]));
  }
  return best;
}

/** Topmost shape whose outline is within `radius` logical units of (px,py). */
export function hitTest(shapes: Shape[], px: number, py: number, radius: number): string | null {
  for (let i = shapes.length - 1; i >= 0; i--) {
    if (distToShape(shapes[i], px, py) <= radius) return shapes[i].id;
  }
  return null;
}

/** Arrowhead barb points for a segment ending at (bx,by), coming from (ax,ay). */
export function arrowHead(ax: number, ay: number, bx: number, by: number, size: number): number[] {
  const ang = Math.atan2(by - ay, bx - ax);
  const a1 = ang + Math.PI - 0.5;
  const a2 = ang + Math.PI + 0.5;
  return [bx + Math.cos(a1) * size, by + Math.sin(a1) * size, bx, by, bx + Math.cos(a2) * size, by + Math.sin(a2) * size];
}

// ── canvas painting ──────────────────────────────────────────────────────────

/** Paint one shape onto a 2D context already transformed into logical space. */
export function paintShape(ctx: CanvasRenderingContext2D, s: Shape): void {
  ctx.strokeStyle = s.color;
  ctx.fillStyle = s.color;
  ctx.lineWidth = s.width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const p = s.points;
  if (s.kind === 'text') {
    const fs = s.fontSize ?? 32;
    ctx.font = `${fs}px ui-sans-serif, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif`;
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(s.text ?? '', p[0], p[1]);
    return;
  }
  if (s.kind === 'rect') {
    const [x0, y0, x1, y1] = p;
    ctx.strokeRect(Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0));
    return;
  }
  if (s.kind === 'ellipse') {
    const [x0, y0, x1, y1] = p;
    ctx.beginPath();
    ctx.ellipse((x0 + x1) / 2, (y0 + y1) / 2, Math.abs(x1 - x0) / 2, Math.abs(y1 - y0) / 2, 0, 0, Math.PI * 2);
    ctx.stroke();
    return;
  }
  if (p.length < 2) return;
  ctx.beginPath();
  ctx.moveTo(p[0], p[1]);
  if (s.kind === 'pen' && p.length >= 6) {
    // Smooth freehand with quadratic segments through the midpoints.
    for (let i = 2; i + 3 < p.length; i += 2) {
      const mx = (p[i] + p[i + 2]) / 2;
      const my = (p[i + 1] + p[i + 3]) / 2;
      ctx.quadraticCurveTo(p[i], p[i + 1], mx, my);
    }
    ctx.lineTo(p[p.length - 2], p[p.length - 1]);
  } else {
    for (let i = 2; i + 1 < p.length; i += 2) ctx.lineTo(p[i], p[i + 1]);
  }
  ctx.stroke();
  if (s.kind === 'arrow' && p.length >= 4) {
    const n = p.length;
    const head = arrowHead(p[n - 4], p[n - 3], p[n - 2], p[n - 1], Math.max(14, s.width * 4));
    ctx.beginPath();
    ctx.moveTo(head[0], head[1]);
    ctx.lineTo(head[2], head[3]);
    ctx.lineTo(head[4], head[5]);
    ctx.stroke();
  }
}

/**
 * Paint the whole board (paper + shapes) onto a context. `dpr` scales the backing
 * store; the context is reset each call. Shapes are drawn in logical units.
 */
export function paintBoard(ctx: CanvasRenderingContext2D, shapes: Shape[], cw: number, ch: number, dpr: number, fit: Fit): void {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cw, ch);
  ctx.save();
  ctx.translate(fit.ox, fit.oy);
  ctx.scale(fit.scale, fit.scale);
  // Paper.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, BOARD_W, BOARD_H);
  // Clip so strokes never spill past the paper edge.
  ctx.beginPath();
  ctx.rect(0, 0, BOARD_W, BOARD_H);
  ctx.clip();
  for (const s of shapes) paintShape(ctx, s);
  ctx.restore();
}

// ── SVG + PNG export (the takeaway artefacts) ─────────────────────────────────

function xmlEscape(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function num(n: number): string {
  return (Math.round(n * 100) / 100).toString();
}

/** Serialise one shape to an SVG element string. */
export function shapeToSvg(s: Shape): string {
  const stroke = `stroke="${s.color}" stroke-width="${num(s.width)}" fill="none" stroke-linecap="round" stroke-linejoin="round"`;
  const p = s.points;
  if (s.kind === 'text') {
    const fs = s.fontSize ?? 32;
    return `<text x="${num(p[0])}" y="${num(p[1])}" font-size="${num(fs)}" fill="${s.color}" font-family="ui-sans-serif, system-ui, sans-serif">${xmlEscape(s.text ?? '')}</text>`;
  }
  if (s.kind === 'rect') {
    const x = Math.min(p[0], p[2]);
    const y = Math.min(p[1], p[3]);
    return `<rect x="${num(x)}" y="${num(y)}" width="${num(Math.abs(p[2] - p[0]))}" height="${num(Math.abs(p[3] - p[1]))}" ${stroke}/>`;
  }
  if (s.kind === 'ellipse') {
    return `<ellipse cx="${num((p[0] + p[2]) / 2)}" cy="${num((p[1] + p[3]) / 2)}" rx="${num(Math.abs(p[2] - p[0]) / 2)}" ry="${num(Math.abs(p[3] - p[1]) / 2)}" ${stroke}/>`;
  }
  if (s.kind === 'line' && p.length >= 4) {
    return `<line x1="${num(p[0])}" y1="${num(p[1])}" x2="${num(p[2])}" y2="${num(p[3])}" ${stroke}/>`;
  }
  // pen or arrow → polyline (+ arrowhead)
  let d = '';
  for (let i = 0; i + 1 < p.length; i += 2) d += `${num(p[i])},${num(p[i + 1])} `;
  let out = `<polyline points="${d.trim()}" ${stroke}/>`;
  if (s.kind === 'arrow' && p.length >= 4) {
    const n = p.length;
    const h = arrowHead(p[n - 4], p[n - 3], p[n - 2], p[n - 1], Math.max(14, s.width * 4));
    out += `<polyline points="${num(h[0])},${num(h[1])} ${num(h[2])},${num(h[3])} ${num(h[4])},${num(h[5])}" ${stroke}/>`;
  }
  return out;
}

/** Serialise the whole board to a standalone SVG document string. */
export function boardToSvg(shapes: Shape[]): string {
  const body = shapes.map(shapeToSvg).join('\n  ');
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${BOARD_W} ${BOARD_H}" width="${BOARD_W}" height="${BOARD_H}">
  <rect width="${BOARD_W}" height="${BOARD_H}" fill="#ffffff"/>
  ${body}
</svg>`;
}
